import { useQuery } from '@tanstack/react-query';
import { useApiMutation } from '../../api/mutations';
import { nexusFetch } from '../../nexus/nexusClient';
import { useFixturesMode, fixtureDelay } from '../../api/fixtures';
import { sagaReviewFixture } from './fixture';

/**
 * Spec: lmm-wiki/wiki/orbit-specs/nexus-saga-review.md
 * Endpoints: /v1/admin/compensations (GET/PATCH/POST :id/retry)
 */

/** GET /v1/admin/compensations?status=&action=&tenant_id= */
export function useSagaCompensations({ status = 'review_required', action = '', tenantId = '' } = {}) {
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['nexus-compensations', { status, action, tenantId }, fixturesMode ? 'fixture' : 'live'],
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return sagaReviewFixture({ status, action, tenantId });
      }
      const qs = new URLSearchParams();
      if (status) qs.set('status', status);
      if (action) qs.set('action', action);
      if (tenantId) qs.set('tenant_id', tenantId);
      const res = await nexusFetch(`/v1/admin/compensations?${qs.toString()}`);
      const list = res?.compensations ?? res?.items ?? (Array.isArray(res) ? res : []);
      return { compensations: list, raw: res };
    },
    refetchInterval: 30_000,
  });
}

/** PATCH /v1/admin/compensations/:id — `{ status: 'done' | 'failed' }` */
export function useUpdateCompensationStatus() {
  return useApiMutation({
    live: (_fetch, { id, status }) =>
      nexusFetch(`/v1/admin/compensations/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: { status },
      }),
    fixture: ({ id, status }) => ({ ok: true, id, status }),
    invalidate: ['nexus-compensations'],
    successMessage: (_, { id, status }) => `Compensation ${id} → ${status}.`,
  });
}

/** POST /v1/admin/compensations/:id/retry */
export function useRetryCompensation() {
  return useApiMutation({
    live: (_fetch, { id }) =>
      nexusFetch(`/v1/admin/compensations/${encodeURIComponent(id)}/retry`, {
        method: 'POST',
      }),
    fixture: ({ id }) => ({ ok: true, id, status: 'pending' }),
    invalidate: ['nexus-compensations'],
    successMessage: (_, { id }) => `Compensation ${id} reaktiviert (pending).`,
  });
}
